import { makeAutoObservable, observable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { TIssue, TIssueSubIssues } from "@/types/issue";
import { classifyPanelFetchError, type PanelFetchError } from "./panel-fetch-error";
import { track, type AnalyticsSource } from "@/lib/analytics";

export class IssueSubIssuesStore {
  subIssuesByIssueId = observable.map<string, TIssueSubIssues>();
  loadingByIssueId = observable.map<string, boolean>();
  errorByIssueId = observable.map<string, PanelFetchError | null>();
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  getSubIssues(issueId: string): TIssue[] {
    const value = this.subIssuesByIssueId.get(issueId);
    if (!value) return [];
    return Array.isArray(value.sub_issues)
      ? value.sub_issues
      : Object.values(value.sub_issues).flat();
  }

  /**
   * Whether this parent's children have been fetched at least once. Distinguishes
   * "no children" (fetched, empty) from "not fetched yet", so callers don't treat
   * an empty result as un-fetched — which would re-fire the fetch on every expand
   * and keep a never-resolving loading row under a parent with zero children.
   */
  hasFetched(issueId: string): boolean {
    return this.subIssuesByIssueId.has(issueId);
  }

  /**
   * Mirror sub-issue snapshots into the root issue store so consumers that
   * read by id (e.g. WorkItemSubtaskPanel via issueStore.issuesById) see
   * updates flow through updateIssue / patchCachedIssue.
   */
  private hydrateIssueStore(container: TIssueSubIssues): void {
    const flat = Array.isArray(container.sub_issues)
      ? container.sub_issues
      : Object.values(container.sub_issues).flat();
    const issueStore = this.rootStore.issue;
    for (const issue of flat) {
      const existing = issueStore.issuesById.get(issue.id);
      issueStore.issuesById.set(issue.id, existing ? { ...existing, ...issue } : issue);
    }
  }

  async fetchSubIssues(workspaceSlug: string, projectId: string, issueId: string): Promise<void> {
    this.loadingByIssueId.set(issueId, true);
    this.errorByIssueId.set(issueId, null);
    try {
      const issues = await this.rootStore.workItemService.listSubWorkItems(
        workspaceSlug,
        projectId,
        issueId,
      );
      runInAction(() => {
        this.subIssuesByIssueId.set(issueId, issues);
        this.hydrateIssueStore(issues);
        // Keep parent's sub_issues_count in sync so the expand chevron renders.
        const flat = Array.isArray(issues.sub_issues)
          ? issues.sub_issues
          : Object.values(issues.sub_issues).flat();
        const parent = this.rootStore.issue.issuesById.get(issueId);
        if (parent) {
          this.rootStore.issue.issuesById.set(issueId, {
            ...parent,
            sub_issues_count: flat.length,
          });
        }
        this.loadingByIssueId.set(issueId, false);
      });
    } catch (error) {
      // A 403 is expected degradation (caller lacks workitem.read on this
      // project) — warn; anything else broke a fetch that should have worked —
      // error. Never swallow; the discriminant is what lets the panel render a
      // "no access" branch instead of a misleading empty state.
      const kind = classifyPanelFetchError(error);
      const log = kind === "forbidden" ? console.warn : console.error;
      log("Failed to fetch sub-issues", { workspaceSlug, projectId, issueId, error });
      runInAction(() => {
        this.loadingByIssueId.set(issueId, false);
        this.errorByIssueId.set(issueId, kind);
      });
    }
  }

  async attachSubIssues(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    subIssueIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const issues = await this.rootStore.workItemService.createSubWorkItem(
      workspaceSlug,
      projectId,
      issueId,
      { sub_issue_ids: subIssueIds },
    );
    runInAction(() => {
      this.subIssuesByIssueId.set(issueId, issues);
      this.hydrateIssueStore(issues);
      this.errorByIssueId.set(issueId, null);
    });
    // Attaching an existing task re-parents it (NG-2056): a task_updated on the
    // task that moved, or one with a count when several moved at once.
    const moved = { project_id: projectId, fields: ["parent_id"], parent_id: issueId, source };
    track(
      "task_updated",
      subIssueIds.length === 1
        ? { ...moved, task_id: subIssueIds[0] }
        : { ...moved, count: subIssueIds.length },
    );
  }

  async createSubIssue(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    data: Partial<TIssue> & { name: string },
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const newIssue = await this.rootStore.issue.createIssue(
      workspaceSlug,
      projectId,
      { ...data, parent_id: issueId },
      source,
    );
    // Optimistically insert the new sub-issue so it renders immediately,
    // then refetch in the background to pick up any server-side defaults
    // (sequence_id, state, etc.) that aren't present on the creation response.
    runInAction(() => {
      const existing = this.subIssuesByIssueId.get(issueId);
      const current = existing
        ? Array.isArray(existing.sub_issues)
          ? existing.sub_issues
          : Object.values(existing.sub_issues).flat()
        : [];
      this.subIssuesByIssueId.set(issueId, {
        sub_issues: [...current, newIssue],
        state_distribution: existing?.state_distribution ?? {
          backlog: [],
          unstarted: [],
          started: [],
          completed: [],
          cancelled: [],
        },
      });
      const parent = this.rootStore.issue.issuesById.get(issueId);
      if (parent) {
        this.rootStore.issue.issuesById.set(issueId, {
          ...parent,
          sub_issues_count: current.length + 1,
        });
      }
    });
    void this.fetchSubIssues(workspaceSlug, projectId, issueId);
  }

  /**
   * Merge an update into any cached copy of `subIssueId` anywhere in the
   * subIssuesByIssueId map. Lets the issue store keep this store in sync
   * after writes so the subtasks table re-renders without a refetch.
   */
  patchCachedIssue(subIssueId: string, patch: Partial<TIssue>): void {
    for (const [parentId, container] of this.subIssuesByIssueId.entries()) {
      if (!container) continue;
      const subIssues = Array.isArray(container.sub_issues)
        ? container.sub_issues
        : Object.values(container.sub_issues).flat();
      const idx = subIssues.findIndex((issue) => issue.id === subIssueId);
      if (idx === -1) continue;
      if (Array.isArray(container.sub_issues)) {
        const next = [...container.sub_issues];
        next[idx] = { ...next[idx], ...patch };
        this.subIssuesByIssueId.set(parentId, { ...container, sub_issues: next });
      } else {
        const grouped = { ...container.sub_issues };
        for (const key of Object.keys(grouped)) {
          const list = grouped[key];
          const i = list.findIndex((issue) => issue.id === subIssueId);
          if (i !== -1) {
            const nextList = [...list];
            nextList[i] = { ...nextList[i], ...patch };
            grouped[key] = nextList;
          }
        }
        this.subIssuesByIssueId.set(parentId, { ...container, sub_issues: grouped });
      }
    }
  }

  /**
   * Drop `subIssueId` from any cached parent's sub-issue list. Used when a
   * child is archived from a table sub-row: the backend's sub-work-items GET
   * no longer returns archived children, so the cached active list must lose
   * the row too — otherwise the archived child keeps rendering until a manual
   * reload. Returns the parent ids whose cached list contained it (so the
   * caller can also keep their `sub_issues_count` in sync).
   */
  removeCachedIssue(subIssueId: string): string[] {
    const affectedParents: string[] = [];
    for (const [parentId, container] of this.subIssuesByIssueId.entries()) {
      if (!container) continue;
      if (Array.isArray(container.sub_issues)) {
        const next = container.sub_issues.filter((issue) => issue.id !== subIssueId);
        if (next.length === container.sub_issues.length) continue;
        affectedParents.push(parentId);
        this.subIssuesByIssueId.set(parentId, { ...container, sub_issues: next });
      } else {
        const grouped = { ...container.sub_issues };
        let removed = false;
        for (const key of Object.keys(grouped)) {
          const next = grouped[key].filter((issue) => issue.id !== subIssueId);
          if (next.length !== grouped[key].length) {
            removed = true;
            grouped[key] = next;
          }
        }
        if (!removed) continue;
        affectedParents.push(parentId);
        this.subIssuesByIssueId.set(parentId, { ...container, sub_issues: grouped });
      }
    }
    return affectedParents;
  }
}
